import { FC, useEffect, useState } from 'react';
import { InlineNotification } from '@carbon/react';
import { useSetAtom } from 'jotai';
import { useStore } from '@tanstack/react-store';

import { useWizardStep } from 'components/full-page-wizard';

import { draftMapFeaturesAtom } from '../atoms';
import { usePackageWizardCtx } from '../context';
import { PackageWizardMap } from '../package-wizard-map';
import { canAdvanceStep2 } from '../utils';
import { Step2ReviewPanel } from './step2-review-panel';
import styles from './summary-step.module.scss';

/**
 * Step 2 — Summary. 2-pane 50/50 split: {@link Step2ReviewPanel} left,
 * {@link PackageWizardMap} with a floating banner right. Gates Next on
 * `packageName` non-blank — mirrors the BE's only mandatory wizard
 * field (the "at least one asset" rule is gated on Step 1; dates and
 * acquiring entities are nullable). Delegates to `api.savePackage` on
 * submit; the api dispatches POST `/packages` (create) or PATCH
 * `/packages/:id` (edit) based on the captured `packageId`, and either
 * branch consumes the draft on the BE.
 *
 * @return {JSX.Element} Step 2 content
 */
export const SummaryStep: FC = () => {
  const { form, api } = usePackageWizardCtx();
  const { setNextEnabled, setOnNext, finishWizard } = useWizardStep();

  const [submitError, setSubmitError] = useState<string | null>(null);

  const canProceed = useStore(form.store, (s) => canAdvanceStep2(s.values));
  const draftId = useStore(form.store, (s) => s.values.draftId);
  const fetchMapFeatures = useSetAtom(draftMapFeaturesAtom);

  useEffect(() => {
    setNextEnabled(canProceed);
  }, [canProceed, setNextEnabled]);

  // Owns the features fetch trigger so the map can be a pure consumer.
  useEffect(() => {
    if (draftId !== null) fetchMapFeatures(draftId);
  }, [draftId, fetchMapFeatures]);

  useEffect(() => {
    setOnNext(async () => {
      const values = form.state.values;

      if (values.draftId === null) {
        throw new Error('Form state incomplete — cannot submit');
      }

      try {
        const saved = await api.savePackage({
          draft_id: values.draftId,
          name: values.packageName.trim(),
          // Create mode: `[]` means inherit from the wizard source packages.
          // Edit mode ignores this payload and diffs via editPackageFragments.
          seller_entity_ids:
            values.sourceType === 'entity'
              ? values.sources.map((source) => source.id)
              : [],
          as_of_date: values.asOfDate,
          effective_date: values.effectiveDate,
          psa_date: values.psaDate,
          close_date: values.closeDate,
          acquirer_entity_ids: values.acquiringEntities.map((e) => e.id),
        });
        setSubmitError(null);
        finishWizard(saved.href);
      } catch (err) {
        setSubmitError(
          err instanceof Error ? err.message : 'Failed to save package'
        );
        throw err;
      }
    });
  }, [form, finishWizard, setOnNext, api]);

  return (
    <div className={styles.layout}>
      <div className={styles.pane}>
        {submitError !== null && (
          <InlineNotification
            kind="error"
            title="Couldn't save package"
            subtitle={submitError}
            lowContrast
            className={styles.errorNotification}
          />
        )}
        <Step2ReviewPanel />
      </div>
      <div className={styles.pane}>
        <PackageWizardMap draftId={draftId} selectedOnly />
      </div>
    </div>
  );
};
